<template>
  <ul class="product-finishes" @mouseenter="handleMouseEnter()" @mouseleave="handleMouseLeave()">
    <li class="indicator" :style="indicatorStyle">
      <div class="ring" />
      <div class="label" :style="labelStyle">
        {{ finish.name | t }}
      </div>
    </li>
    <li
      v-for="(finish, index) in finishes"
      :key="finish.key"
      :class="{ [finish.key]: true, active: currentIndex === index }"
      @mouseenter="enter(index)"
    />
  </ul>
</template>

<script>
import t from '../main/i18n';

export default {
  props: {
    finishes: Array,
    finish: Object
  },
  filters: {
    t
  },
  data() {
    return {
      currentIndex: 0,
      timeout: null,
      imagesLoaded: false,
      hover: false,
      labelsX: 0
    };
  },
  watch: {
    finish: {
      handler() {
        const i = this.finishes.findIndex(v => v.key === this.finish.key);
        this.currentIndex = i >= 0 ? i : 0;
      },
      immediate: true
    }
  },
  computed: {
    labelStyle() {
      if (this.finish) {
        return {maxWidth: `${t(this.finish.name).length + 1}ch`};
      }
      return {maxWidth: '15ch'};
    },
    indicatorStyle() {
      const i = this.currentIndex;
      let transformNumber;
      if (window.innerWidth < 768) {
        transformNumber = 16;
      } else {
        transformNumber = 24;
      }
      const x = i * transformNumber + i * 10;
      return {transform: `translateX(${x}px)`};
    }
  },
  methods: {
    enter(i) {
      const finish = this.finishes[i];
      this.$emit('product-finish-change', finish);
    },
    preloadImages() {
      if (this.timeout) return;
      // wrapped in timeout ensure user is intentionally hovering
      this.timeout = setTimeout(() => {
        Promise.all(
          Object.values(this.finishes).map(src => {
            return new Promise(resolve => {
              const img = new Image();
              img.src = src.image;
              img.onload = resolve;

            });
          })
        ).then(() => {
          this.imagesLoaded = true;
        });
        clearTimeout(this.timeout);
      }, 150);
    },
    handleMouseEnter() {
      this.hover = true;
      if (this.imagesLoaded) return;
      this.preloadImages();
      this.$emit('finish-mouseenter');
    },
    handleMouseLeave() {
      this.hover = false;
      this.$emit('finish-mouseleave');
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../scss/variables';
@import '../../scss/system';

$finish-chrome: #f2f4f6;
$finish-nickel: #e2ded4;
$finish-brass: #efdaa3;
$finish-stainless: #dadcde;
$finish-graphite: #5f5f5f;
$finish-black: #323232;
$finish-white: #f8f8f8;

.product-finishes {
  display: inline-flex;
  position: relative;
  margin: 0 -10px;
  padding: 16px 10px;
  list-style: none;
  font-size: 0;
  cursor: pointer;

  @media (min-width: $screen-sm) {
    padding: 0 10px 16px;
  }

  li {
    display: inline-block;
    position: relative;
    width: 16px;
    height: 16px;
    margin-right: 10px; // IE11
    border: 2px solid transparent;
    border-radius: 50%;
    transform: scale(1);
    transition: transform 150ms cubic-bezier(0.445, 0.05, 0.55, 0.95);
    box-shadow: 0 0 0 1px $color-gray-400;

    @media (min-width: $screen-sm) {
      width: 24px;
      height: 24px;
    }

    &.active {
      transform: scale(0.8);
    }
    &:last-child {
      margin-right: 0;
    }
  }

  &:hover .indicator .ring {
    border-color: $sloan-blue;
  }

  &:hover .indicator .label {
    opacity: 1;
    transform: translate(-50%, 0);
  }

  .indicator {
    position: absolute;
    width: 16;
    z-index: 1;
    transition: transform 200ms cubic-bezier(0.165, 0.84, 0.44, 1);
    pointer-events: none;

    @media (min-width: $screen-sm) {
      width: 24px;
    }

    .ring {
      position: absolute;
      top: -4px;
      left: -4px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 2px solid $gray-base;

      @media (min-width: $screen-sm) {
        width: 28px;
        height: 28px;
      }
    }

    .label {
      position: absolute;
      top: 32px;
      left: 50%;
      font-size: $font-size-small;
      font-weight: 400;
      white-space: nowrap;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background: black;
      color: white;
      width: 100vw;
      height: 1.75em;
      transform: translate(-50%, 4px);
      border-radius: 3px;
      transition:
        max-width 300ms cubic-bezier(0.165, 0.84, 0.44, 1),
        opacity 200ms cubic-bezier(0.23, 1, 0.32, 1),
        transform 120ms cubic-bezier(0.23, 1, 0.32, 1);
      text-align: center;
      padding: 0;
      opacity: 0;
    }
  }

  .polished-chrome {
    background: $finish-chrome;
  }

  .brushed-nickel {
    background: $finish-nickel;
  }

  .brushed-stainless {
    background: $finish-stainless;
  }

  .polished-brass {
    background: $finish-brass;
  }

  .graphite {
    background: $finish-graphite;
    box-shadow: 0 0 0 1px $finish-graphite;
  }

  .matte-black {
    background: $finish-black;
    box-shadow: 0 0 0 1px $finish-black;
  }

  .matte-white {
    background: $finish-white;
  }

  .black {
    background: $finish-black;
  }

  .stainless {
    background: $finish-stainless;
  }
}
</style>
